import { Component, OnDestroy, OnInit } from '@angular/core';
import { GiftCardPurchaseFormService } from './gift-card-purchase-form/gift-card-purchase-form.service';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { Apollo, gql } from 'apollo-angular';
import { CheckoutState } from '../checkout/checkout.state';
import { of, Subject } from 'rxjs';
import { catchError, takeUntil, tap } from 'rxjs/operators';
import { NotificationService } from 'src/app/shared/notification/notification.service';
import { CustomAmountSettings, Design } from './design-and-amount-settings';
import { LaunchDarklyFeatureKey } from 'src/app/shared/launch-darkly-feature-key';
import { LaunchDarklyService } from 'src/app/services/launch-darkly-service/launch-darkly-service';

const query = gql`
    query Query($customerID: Int!) {
        giftCardSettings(customerID: $customerID) {
            policy
            expirationDays
            suggestedAmounts {
                amount
            }
            designs {
                id
                image
                isFeatured
                category {
                    name
                    displayOrder
                }
            }
            customAmount {
                isEnabled
                maximum
                minimum
            }
        }

        olsCheckoutState @client {
            itemImagePath
            recipientName
            recipientEmail
            itemTotal
            itemNote
            senderName
            senderEmail
            giftCardDesignID
        }
    }
`;

export enum RecipientTab {
    FOR_ME,
    FOR_SOMEONE_ELSE,
}

@Component({
    selector: 'ols-gift-card-purchase',
    templateUrl: './gift-card-purchase.component.html',
    styleUrls: ['./gift-card-purchase.component.scss'],
    providers: [GiftCardPurchaseFormService],
})
export class GiftCardPurchaseComponent implements OnInit, OnDestroy {
    form: FormGroup;
    selectedRecipientTabIndex = RecipientTab.FOR_ME;
    designs: Design[];
    suggestedAmounts: number[];
    customAmountSettings: CustomAmountSettings;
    expirationDays: number;
    private ngUnsubscribe: Subject<any> = new Subject();
    constructor(
        private giftCardPurchaseFormService: GiftCardPurchaseFormService,
        private router: Router,
        private apollo: Apollo,
        private notificationService: NotificationService,
        private launchDarklyService: LaunchDarklyService
    ) {}

    ngOnInit(): void {
        this.form = this.giftCardPurchaseFormService.createForm();

        this.apollo
            .watchQuery({ query })
            .valueChanges.pipe(
                takeUntil(this.ngUnsubscribe),
                tap((response: any) => {
                    if (!response?.data?.giftCardSettings) {
                        this.notificationService.showFailure(response.errors);
                        return;
                    }
                    this.designs = response.data.giftCardSettings.designs
                        .slice()
                        .sort((a, b) => b.isFeatured - a.isFeatured);
                    this.suggestedAmounts = response.data.giftCardSettings.suggestedAmounts
                        .map((amountObject) => amountObject.amount.toFixed(2))
                        .sort((first, second) => first - second);
                    this.customAmountSettings =
                        response.data.giftCardSettings.customAmount;
                    this.expirationDays =
                        response.data.giftCardSettings.expirationDays;
                    setTimeout(() => {
                        //Using setTimeout to prevent the form data from being set until all inputs have been updated first
                        if (response.data.olsCheckoutState) {
                            this.giftCardPurchaseFormService.setData(
                                response.data.olsCheckoutState
                            );

                            if (
                                this.form.controls.recipientName?.value ===
                                    this.form.controls.senderName?.value &&
                                this.form.controls.senderEmail?.value ===
                                    this.form.controls.recipientEmail?.value
                            ) {
                                this.selectedRecipientTabIndex =
                                    RecipientTab.FOR_ME;
                                this.giftCardPurchaseFormService.clearRecipientOptionValidators();
                            } else {
                                this.selectedRecipientTabIndex =
                                    RecipientTab.FOR_SOMEONE_ELSE;
                            }
                        }

                        this.setDefaultValues();
                        this.giftCardPurchaseFormService.setItemTotalValidators(
                            this.customAmountSettings,
                            this.suggestedAmounts
                        );
                    });
                }),
                catchError((error) => {
                    this.notificationService.showFailure(error);
                    return of();
                })
            )
            .subscribe();

        this.giftCardPurchaseFormService
            .formSubmitted$()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((_) => {
                this.checkout();
            });
    }

    private setDefaultValues() {
        if (!this.giftCardPurchaseFormService.itemTotal.value) {
            this.giftCardPurchaseFormService.itemTotal.setValue(
                this.suggestedAmounts[0]
            );
        }

        if (!this.giftCardPurchaseFormService.giftCardDesignID.value) {
            this.giftCardPurchaseFormService.selectDesign(this.designs[0]);
        }
    }

    submitForm() {
        this.giftCardPurchaseFormService.submitForm();
    }

    isCheckBalanceFeatureEnabled() {
        return this.launchDarklyService.isFeatureActive(
            LaunchDarklyFeatureKey.GiftCardBalance
        );
    }

    private checkout() {
        if (this.giftCardPurchaseFormService.isValid()) {
            const giftCardInfo = this.giftCardPurchaseFormService.getData();
            const senderName = giftCardInfo.senderLastName.trim()
                ? `${giftCardInfo.senderFirstName}||${giftCardInfo.senderLastName}`
                : giftCardInfo.senderFirstName;
            CheckoutState({
                recipientEmail: giftCardInfo.recipientEmail
                    ? giftCardInfo.recipientEmail
                    : giftCardInfo.senderEmail,
                recipientName: giftCardInfo.recipientName
                    ? giftCardInfo.recipientName
                    : giftCardInfo.recipientName,
                senderName: senderName,
                senderEmail: giftCardInfo.senderEmail,
                itemNote: giftCardInfo.itemNote,
                itemTotal: giftCardInfo.itemTotal,
                itemImagePath: giftCardInfo.itemImagePath,
                giftCardDesignID: giftCardInfo.giftCardDesignID,
                expirationDays: this.expirationDays,
            });
            this.router.navigate(['/checkout']);
            return;
        }
    }

    goToGiftCardBalancePage(): void {
        this.router.navigate(['/gift/balance']);
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
}
